import type { Field, Form } from 'formik'
import type { Dispatch, ReactElement, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import type { GroupBase, MultiValueProps } from 'react-select'
import { components } from 'react-select'
import { AsyncPaginate } from 'react-select-async-paginate'
import type {
  ComponentProps,
  UseAsyncPaginateParams,
} from 'react-select-async-paginate'

import reactSelectStyles from './filterConditionStyles'
import type { DropdownOption, NegativeInfoDropdownOption } from '../types'

type customType = {
  selectProps: {
    canBeNegative?: boolean
    setValues: Dispatch<SetStateAction<any>>
  }
}

const CustomMultiValue = (props: MultiValueProps<any> & customType) => {
  const isNegative = props.getValue()[props.index]?.isNegative

  const onButtonClick = (e: any) => {
    e.stopPropagation()

    props?.selectProps.setValues((values: any[]) => {
      const list = [...values]
      if (list[props.index].isNegative) {
        list[props.index].isNegative = false
      } else {
        list[props.index].isNegative = true
      }
      return list
    })
  }

  return (
    <components.MultiValue {...props}>
      <button
        type="button"
        onClick={onButtonClick}
        className={`${isNegative ? 'line-through' : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {props.children}
      </button>
    </components.MultiValue>
  )
}

type AsyncPaginateProps<
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean,
> = UseAsyncPaginateParams<OptionType, Group, Additional> &
  ComponentProps<OptionType, Group, IsMulti> & {
    className?: string
    onChange: { (opt: NegativeInfoDropdownOption[] | null): void }
    defaultValue: DropdownOption
    isSearchable: boolean
    styles: any
    components?: any
    value?: DropdownOption[]
    isClearable?: boolean
    isMulti?: boolean
    onKeyDown: (e: any) => void
    setValues: Dispatch<SetStateAction<any>>
    menuPortalTarget?: HTMLElement | null
    menuPosition?: 'fixed' | 'absolute'
  }

type AsyncPaginateType = <
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean = false,
>(
  props: AsyncPaginateProps<OptionType, Group, Additional, IsMulti>,
) => ReactElement

const CustomAsyncPaginate = AsyncPaginate as AsyncPaginateType

export default function FilterAsyncPaginateDropdown({
  fetcher,
  onChange,
  onSubmit,
  defaultValue,
  form,
  field,
  isClearable,
  value,
  isReady = false,
  ...rest
}: {
  fetcher: any // TODO: fix type issue and change type to: { (args: PaginatedQueryParams): Promise<PaginatedResponse<any>> }
  onChange: any
  onSubmit?: () => void
  form?: typeof Form
  field?: typeof Field
  defaultValue: any
  isClearable?: boolean
  isReady: boolean
  value?: DropdownOption[]
}) {
  const [values, setValues] = useState(defaultValue ? defaultValue : [])

  useEffect(() => {
    if (!defaultValue || defaultValue.length === 0) {
      setValues([])
    } else if (
      !defaultValue.every(
        (item: DropdownOption) =>
          values.findIndex((v: DropdownOption) => v.value === item.value) > -1,
      )
    ) {
      setValues(defaultValue)
    }
  }, [defaultValue])

  useEffect(() => {
    if (isReady && values) {
      onChange?.(values)
    }
  }, [values])

  const loadOptions: any = async (
    search: string,
    loadedOptions: any,
    { page, hasMore }: { page: number; hasMore: boolean },
  ) => {
    try {
      const { data, meta } = await fetcher({ page, limit: 10, search })
      return {
        options: data,
        hasMore: meta.page < meta.totalPages,
        additional: {
          page: meta.page + 1,
        },
      }
    } catch (error) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      }
    }
  }

  return (
    <CustomAsyncPaginate
      className="w-full text-sm font-semibold"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      loadOptions={loadOptions}
      onChange={(opt: NegativeInfoDropdownOption[] | null) => {
        const valueList = opt?.map((item) => {
          const found = values.find(
            (i: NegativeInfoDropdownOption) => i.value === item.value,
          )
          if (found) {
            item.isNegative = found.isNegative
          } else {
            item.isNegative = false
          }
          return item
        })
        setValues(valueList)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onSubmit) {
          onSubmit()
        }
      }}
      defaultValue={defaultValue}
      isSearchable={true}
      styles={reactSelectStyles}
      additional={{
        page: 1,
        hasMore: true,
      }}
      components={{
        MultiValue: CustomMultiValue,
      }}
      isClearable={true}
      isMulti={true}
      value={values}
      setValues={setValues}
      {...rest}
    />
  )
}
